import { AlertCircle, ChevronRight, Filter, Search, X } from 'lucide-react';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CatalogFilterInfoBanner } from '~/components/common/catalog-filter-info-banner';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~/components/ui/sheet';
import { Skeleton } from '~/components/ui/skeleton';
import { Switch } from '~/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import { useProductFilters } from '../../../hooks/use-product-metadata';
import type {
  AppliedProductFilters,
  ProductFilterDef,
  ProductFiltersParams,
} from '../../../types/product-metadata.types';
import {
  areAppliedFiltersEqual,
  isLockedLastValue,
  omitAppliedFilter,
} from '../../../utils/applied-filters';
import { buildFilterOptionLabelsForKey } from '../../../utils/filter-option-label';

import { PaginatedFilterOptions } from './paginated-filter-options';

/** Above this many options a multi_select shows a search box and drops "Select all". */
const OPTIONS_SEARCH_THRESHOLD = 50;

// has_item_issues is not yet implemented in the product-list API — hide until backend supports it.
// product_id / item_group_id / mpn are ID lookup fields not suitable for catalog scoping.
const HIDDEN_FILTER_KEYS = new Set(['product_id', 'item_group_id', 'mpn']);

/**
 * Descriptive product-attribute filters suppressed when
 * `hideProductAttributeFilters` is set (Analytics Setup / step 3). These narrow
 * by garment properties, which is a scoping concern owned by Define Scope —
 * step 3 only carves out a reporting segment, so they add noise there.
 *
 * Matched on key *or* normalised label: the filter list is backend-driven and
 * only `color` is pinned by a fixture, so the label check covers key naming we
 * can't verify from this repo.
 */
const PRODUCT_ATTRIBUTE_FILTER_KEYS = new Set([
  'color',
  'material',
  'pattern',
  'condition',
  'quality_grade',
]);
const PRODUCT_ATTRIBUTE_FILTER_LABELS = new Set([
  'color',
  'material',
  'pattern',
  'condition',
  'quality grade',
]);

/** Whether a filter def is one of the descriptive product-attribute filters. */
function isProductAttributeFilter(def: ProductFilterDef): boolean {
  return (
    PRODUCT_ATTRIBUTE_FILTER_KEYS.has(def.key) ||
    PRODUCT_ATTRIBUTE_FILTER_LABELS.has(def.label.trim().toLowerCase())
  );
}

/**
 * Custom-label filter definitions hardcoded here so they always appear in the
 * drawer regardless of whether POST /products/filters returns them. Merged at
 * render time and deduplicated against the API response in case the backend
 * starts returning them. Filter keys use snake_case to match the backend API contract.
 */
const CUSTOM_LABEL_FILTERS: ProductFilterDef[] = ['0', '1', '2', '3', '4'].map((i) => ({
  key: `custom_label_${i}`,
  label: `Custom Label ${i}`,
  column: `custom_label_${i}`,
  type: 'text_contains' as const,
  options: null,
  min: null,
  max: null,
  predicate: null,
}));

interface Props {
  connectorCode: string | null;
  connectionId: string | null;
  applied: AppliedProductFilters;
  onApply: (applied: AppliedProductFilters) => void;
  /** Selected sync/revision to scope the filter defs + options to (null =
   *  latest). Passed by callers that own a revision picker (e.g. Product
   *  Catalog); the experiment wizard omits it and reads the latest snapshot. */
  syncId?: string | null;
  /**
   * Snapshot of applied filters used **once** as the body for the filter-defs
   * API call. Captured at mount and never updated — toggling/applying inside
   * the drawer does not re-fetch the filter list. Defaults to `{}` (step-2
   * behaviour: a fully un-narrowed filter set). Step 3 passes the filters
   * persisted from step 2 so its filter list reflects the upstream scope.
   */
  seedAppliedFilters?: AppliedProductFilters;
  /**
   * When true, paginated filters whose initial `options` array is empty are
   * hidden. Step 3 (Analytics Setup) enables this because its filter-def
   * request is scoped to step-2's narrowed product set — a paginated filter
   * with no initial options has nothing to show.
   */
  hideEmptyPaginatedFilters?: boolean;
  /** When true, the hardcoded customLabel0–4 filters are excluded. Use on
   *  pages where custom-label fields are not relevant to the task (e.g. Meta
   *  product selection). */
  hideCustomLabels?: boolean;
  /**
   * When true, the descriptive product-attribute filters (Color, Material,
   * Pattern, Condition, Quality Grade) are excluded. Set by step 3 (Analytics
   * Setup), where the task is carving a reporting segment rather than scoping
   * the catalog by garment properties.
   */
  hideProductAttributeFilters?: boolean;
  /** When true, the trigger button shows a badge with the number of applied
   *  filters. Off by default (the wizard surfaces applied filters as chips). */
  showAppliedCount?: boolean;
  /** Locked (default) filter keys: their value can change, but the last selected
   *  value can't be unticked, so the filter never empties. */
  lockedFilterKeys?: ReadonlySet<string>;
  /** What "Reset Filters" restores the draft to (the page's defaults); `{}` when
   *  omitted. The button disables once the draft already matches it. */
  resetFilters?: AppliedProductFilters;
  /** When given, only these filter keys are offered — for hosts that can only apply some
   *  filters (e.g. the ones a batch can take). Omit to offer every filter. */
  allowedFilterKeys?: ReadonlySet<string>;
  /** E2E `data-testid` root (the sheet); trigger, sections and options derive `${testId}-…`. */
  testId?: string;
}

/** Stable empty map — the reset target for callers without default filters. */
const NO_FILTERS: AppliedProductFilters = {};

/** Filter option value → kebab-case test-id segment (raw value also lands on `data-value`). */
function toOptionTestIdSlug(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'empty'
  );
}

/**
 * Imperative handle exposed via `ref` so parents (the "+N filters" overflow
 * pill in {@link FilterChipsStrip}, for example) can open the drawer without
 * lifting its full open-state up.
 */
export interface CatalogFiltersDrawerHandle {
  open: () => void;
}

/**
 * Filters drawer — renders dynamic filter definitions from `POST /products/filters`.
 * Each filter is rendered by `type`: multi_select → collapsible checkbox list,
 * boolean → always-visible switch, text_contains → text input. Selections are
 * tracked as `AppliedProductFilters` keyed by `ProductFilterDef.key` and sent
 * back to the product-list API on apply.
 *
 * The filter list itself is fetched **once** (per mount) using
 * `seedAppliedFilters` — the drawer never re-fetches in response to user
 * interaction. Both step 2 and step 3 are stable filter lists; the only
 * difference is what seed they use.
 */
export const CatalogFiltersDrawer = forwardRef<CatalogFiltersDrawerHandle, Props>(
  function CatalogFiltersDrawer(
    {
      connectorCode,
      connectionId,
      applied,
      onApply,
      syncId = null,
      seedAppliedFilters,
      hideEmptyPaginatedFilters = false,
      hideCustomLabels = false,
      hideProductAttributeFilters = false,
      showAppliedCount = false,
      lockedFilterKeys,
      resetFilters = NO_FILTERS,
      allowedFilterKeys,
      testId = 'catalog-filters-drawer',
    }: Props,
    ref,
  ) {
    const { t } = useTranslation('experiments');
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(applied);
    const [expandedKey, setExpandedKey] = useState<string | null>(null);

    // Number of committed (applied) filters — drives the trigger badge. Empty
    // values are already omitted from `applied` on apply, but guard anyway.
    const appliedCount = useMemo(
      () =>
        Object.values(applied).filter(
          (v) => v !== null && v !== '' && v !== false && !(Array.isArray(v) && v.length === 0),
        ).length,
      [applied],
    );

    useImperativeHandle(ref, () => ({
      open: () => {
        setOpen(true);
      },
    }));

    // Lazy-init `useState` captures the seed applied-filters once at mount.
    // The setter is intentionally unused — the filter-defs API stays bound
    // to whatever upstream selections existed when the screen was opened,
    // so toggling/applying inside the drawer never re-fetches the list.
    const [seedFilters] = useState<AppliedProductFilters>(() => seedAppliedFilters ?? {});

    const params: ProductFiltersParams | null =
      connectorCode && connectionId
        ? { connectorCode, connectionId, syncId, appliedFilters: seedFilters }
        : null;
    const { data: filterDefs, isLoading, isError, refetch } = useProductFilters(params);

    // Merge API filter defs with hardcoded custom-label filters. API version wins
    // if the backend ever starts returning these keys (deduplication by key).
    // `hideCustomLabels` suppresses the hardcoded set entirely for callers
    // (e.g. Meta product selection) where these fields are not relevant.
    const allFilterDefs = useMemo(() => {
      const apiKeys = new Set((filterDefs ?? []).map((d) => d.key));
      const extra = hideCustomLabels ? [] : CUSTOM_LABEL_FILTERS.filter((f) => !apiKeys.has(f.key));
      return [...(filterDefs ?? []), ...extra];
    }, [filterDefs, hideCustomLabels]);

    const handleOpen = (isOpen: boolean) => {
      if (isOpen) setDraft(applied);
      setOpen(isOpen);
    };

    const setValue = (key: string, value: AppliedProductFilters[string]) => {
      setDraft((prev) => {
        const isEmpty =
          value === null ||
          value === '' ||
          value === false ||
          (Array.isArray(value) && value.length === 0);
        return isEmpty ? omitAppliedFilter(prev, key) : { ...prev, [key]: value };
      });
    };

    const toggleMultiValue = (key: string, option: string) => {
      // A locked filter's last value stays put — the checkbox is disabled too.
      if (isLockedLastValue(lockedFilterKeys, draft, key, option)) return;
      const current = (draft[key] as string[] | undefined) ?? [];
      const has = current.includes(option);
      setValue(key, has ? current.filter((v) => v !== option) : [...current, option]);
    };

    const toggleSelectAll = (key: string, options: string[]) => {
      const current = (draft[key] as string[] | undefined) ?? [];
      const allSelected = options.every((opt) => current.includes(opt));
      // Deselecting everything would empty a locked filter.
      if (allSelected && lockedFilterKeys?.has(key)) return;
      setValue(key, allSelected ? [] : [...options]);
    };

    const handleApply = () => {
      onApply(draft);
      setOpen(false);
    };

    const handleReset = () => {
      setDraft(resetFilters);
    };
    const isDraftAtReset = areAppliedFiltersEqual(draft, resetFilters);

    return (
      <Sheet open={open} onOpenChange={handleOpen}>
        {/* Below 1080px the label collapses to icon-only; the tooltip is suppressed
            above that breakpoint so it doesn't duplicate the visible label. */}
        <TooltipProvider>
          <Tooltip>
            <SheetTrigger asChild>
              <TooltipTrigger asChild>
                <Button data-testid={`${testId}-trigger-button`} className="cursor-pointer gap-2">
                  <Filter className="h-4 w-4" />
                  <span className="hidden min-[1180px]:inline">
                    {t('create.step2.scope.filter')}
                  </span>
                  {showAppliedCount && appliedCount > 0 && (
                    <span
                      data-testid={`${testId}-trigger-count`}
                      className="bg-primary-foreground text-primary inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums"
                    >
                      {appliedCount}
                    </span>
                  )}
                </Button>
              </TooltipTrigger>
            </SheetTrigger>
            <TooltipContent className="min-[1180px]:hidden">
              {t('create.step2.scope.filter')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <SheetContent data-testid={testId} side="right" className="flex w-[400px] flex-col p-0">
          <SheetHeader className="border-b px-6 py-4">
            <SheetTitle
              data-testid={`${testId}-title`}
              className="flex items-center gap-2 text-base"
            >
              <Filter className="h-5 w-5" />
              {t('create.step2.filters.title')}
            </SheetTitle>
            <SheetDescription className="sr-only">
              {t('create.step2.filters.title')}
            </SheetDescription>
          </SheetHeader>

          <CatalogFilterInfoBanner className="mx-6 mt-3" />

          <div data-testid={`${testId}-body`} className="flex-1 overflow-y-auto">
            {isLoading && <FiltersLoading testId={testId} />}
            {isError && (
              <FiltersError
                testId={testId}
                onRetry={() => {
                  void refetch();
                }}
              />
            )}
            {!isLoading && !isError && allFilterDefs.length === 0 && (
              <FiltersEmpty testId={testId} />
            )}
            {!isLoading &&
              !isError &&
              allFilterDefs
                .filter((def) => !HIDDEN_FILTER_KEYS.has(def.key))
                .filter((def) => !allowedFilterKeys || allowedFilterKeys.has(def.key))
                .filter((def) => !hideProductAttributeFilters || !isProductAttributeFilter(def))
                .map((def) => (
                  <FilterRow
                    key={def.key}
                    testId={testId}
                    def={def}
                    connectorCode={connectorCode}
                    connectionId={connectionId}
                    seedFilters={seedFilters}
                    syncId={syncId}
                    hideEmptyPaginatedFilters={hideEmptyPaginatedFilters}
                    value={draft[def.key]}
                    locked={lockedFilterKeys?.has(def.key) ?? false}
                    isExpanded={expandedKey === def.key}
                    onToggleExpand={() => {
                      setExpandedKey((prev) => (prev === def.key ? null : def.key));
                    }}
                    onSetValue={(v) => {
                      setValue(def.key, v);
                    }}
                    onToggleMultiValue={(opt) => {
                      toggleMultiValue(def.key, opt);
                    }}
                    onToggleSelectAll={(options) => {
                      toggleSelectAll(def.key, options);
                    }}
                  />
                ))}
          </div>

          <SheetFooter className="border-t px-6 py-4">
            <Button
              data-testid={`${testId}-reset-button`}
              variant="outline"
              className="flex-1 cursor-pointer disabled:cursor-not-allowed"
              onClick={handleReset}
              disabled={isDraftAtReset}
            >
              {t('create.step2.filters.resetFilters')}
            </Button>
            <Button
              data-testid={`${testId}-apply-button`}
              className="flex-1 cursor-pointer"
              onClick={handleApply}
              disabled={isLoading || isError}
            >
              {t('create.step2.filters.applyFilters')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    );
  },
);

/**
 * Render one filter definition by its `type`. Kept as a separate component
 * so multi_select's collapse state can live next to the rest of its UI and
 * the parent doesn't branch on type for every callback.
 */
function FilterRow({
  testId,
  def,
  connectorCode,
  connectionId,
  seedFilters,
  syncId,
  hideEmptyPaginatedFilters,
  value,
  locked,
  isExpanded,
  onToggleExpand,
  onSetValue,
  onToggleMultiValue,
  onToggleSelectAll,
}: {
  /** Drawer test-id root; this row is `${testId}-section-${def.key}`. */
  testId: string;
  def: ProductFilterDef;
  connectorCode: string | null;
  connectionId: string | null;
  seedFilters: AppliedProductFilters;
  syncId: string | null;
  hideEmptyPaginatedFilters: boolean;
  value: AppliedProductFilters[string] | undefined;
  /** Locked (default) filter — must always keep at least one value. */
  locked: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onSetValue: (v: AppliedProductFilters[string]) => void;
  onToggleMultiValue: (opt: string) => void;
  onToggleSelectAll: (options: string[]) => void;
}) {
  const { t } = useTranslation('experiments');
  const sectionTestId = `${testId}-section-${def.key}`;

  if (def.type === 'boolean') {
    return (
      <div data-testid={sectionTestId} className="border-b px-6 py-3">
        <div className="bg-surface-stat-card flex items-center justify-between rounded-xl p-3">
          <Label htmlFor={`filter-${def.key}`} className="cursor-pointer text-sm font-medium">
            {def.label}
          </Label>
          <Switch
            data-testid={`${sectionTestId}-switch`}
            id={`filter-${def.key}`}
            checked={value === true}
            onCheckedChange={(checked) => {
              onSetValue(checked);
            }}
          />
        </div>
      </div>
    );
  }

  if (def.type === 'text_contains' || def.type === 'text') {
    const textValue = typeof value === 'string' ? value : '';
    return (
      <div data-testid={sectionTestId} className="space-y-2 border-b px-6 py-3">
        <Label htmlFor={`filter-${def.key}`} className="text-sm font-medium">
          {def.label}
        </Label>
        <div className="bg-surface-stat-card relative rounded-xl p-3">
          <Input
            data-testid={`${sectionTestId}-input`}
            id={`filter-${def.key}`}
            value={textValue}
            onChange={(e) => {
              onSetValue(e.target.value);
            }}
            placeholder={def.label}
            className={cn('bg-white shadow-none', textValue && 'pr-8')}
          />
          {textValue && (
            <button
              data-testid={`${sectionTestId}-clear-button`}
              type="button"
              aria-label={t('create.step2.filters.clearTextFilter')}
              className="text-muted-foreground hover:text-foreground absolute top-1/2 right-5 -translate-y-1/2 cursor-pointer"
              onClick={() => {
                onSetValue('');
              }}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // multi_select — collapsible checkbox list. Paginated filters fetch their
  // options lazily on expand; small inline ones render their `options` array.
  // Always hide non-paginated filters with zero options. For paginated filters,
  // also hide when `hideEmptyPaginatedFilters` is set (step 3 / Analytics Setup)
  // — the filter-defs API there is scoped to step-2's narrowed product set, so
  // a paginated filter with no initial options has nothing to offer.
  const hasNoOptions = !def.options || def.options.length === 0;
  if (hasNoOptions && (!def.paginated || hideEmptyPaginatedFilters)) return null;

  const current = (value as string[] | undefined) ?? [];
  return (
    <Collapsible
      data-testid={sectionTestId}
      open={isExpanded}
      onOpenChange={() => {
        onToggleExpand();
      }}
      className="border-b"
    >
      <CollapsibleTrigger asChild>
        <button
          data-testid={`${sectionTestId}-toggle`}
          type="button"
          className="hover:bg-muted/50 flex w-full cursor-pointer items-center justify-between px-6 py-3 text-sm font-medium"
        >
          <span>
            {def.label}
            {current.length > 0 && (
              <span
                data-testid={`${sectionTestId}-count`}
                className="text-muted-foreground ml-2 text-xs font-normal"
              >
                ({current.length})
              </span>
            )}
          </span>
          <ChevronRight
            className={cn(
              'text-muted-foreground h-4 w-4 transition-transform duration-200',
              isExpanded && 'rotate-90',
            )}
          />
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent data-testid={`${sectionTestId}-options`}>
        {def.paginated ? (
          <PaginatedFilterOptions
            testId={testId}
            def={def}
            connectorCode={connectorCode}
            connectionId={connectionId}
            seedFilters={seedFilters}
            syncId={syncId}
            selected={current}
            onToggle={onToggleMultiValue}
            locked={locked}
          />
        ) : (
          <StaticMultiSelectOptions
            testId={testId}
            def={def}
            selected={current}
            locked={locked}
            onToggleMultiValue={onToggleMultiValue}
            onToggleSelectAll={onToggleSelectAll}
          />
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Inline option list for a non-paginated multi_select — the full `options`
 * array is already present. Adds a client-side search box and drops the
 * "Select all" affordance once the list exceeds {@link OPTIONS_SEARCH_THRESHOLD}.
 */
function StaticMultiSelectOptions({
  testId,
  def,
  selected,
  locked,
  onToggleMultiValue,
  onToggleSelectAll,
}: {
  /** Drawer test-id root; options are `${testId}-option-${def.key}-${value-slug}`. */
  testId: string;
  def: ProductFilterDef;
  selected: string[];
  locked: boolean;
  onToggleMultiValue: (opt: string) => void;
  onToggleSelectAll: (options: string[]) => void;
}) {
  const { t } = useTranslation('experiments');
  const [search, setSearch] = useState('');
  const allOptions = useMemo(() => def.options ?? [], [def.options]);
  // Collision-aware display labels computed over the full set (stable while
  // searching). Breadcrumb filters (product type) keep their full path.
  const labels = useMemo(
    () => buildFilterOptionLabelsForKey(def.key, allOptions),
    [def.key, allOptions],
  );
  const showSearch = allOptions.length > OPTIONS_SEARCH_THRESHOLD;
  const showSelectAll = allOptions.length <= OPTIONS_SEARCH_THRESHOLD;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? allOptions.filter((opt) => opt.toLowerCase().includes(q)) : allOptions;
  }, [allOptions, search]);

  if (allOptions.length === 0) return null;

  // A locked filter must keep one value: its only selected option is pinned, and
  // "Select all" can't clear it once everything is ticked. A single-option locked
  // filter is therefore read-only.
  const pinnedOption = locked && selected.length === 1 ? selected[0] : undefined;
  const allSelected = allOptions.every((opt) => selected.includes(opt));
  const selectAllLocked = locked && allSelected;
  const sectionTestId = `${testId}-section-${def.key}`;

  return (
    <div className="space-y-2 px-6 pt-1 pb-3">
      {showSearch && (
        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
          <Input
            data-testid={`${sectionTestId}-search-input`}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            aria-label={t('create.step2.filters.optionsSearchPlaceholder', { label: def.label })}
            placeholder={t('create.step2.filters.optionsSearchPlaceholder', { label: def.label })}
            className="pr-8 pl-8"
          />
          {search && (
            <button
              data-testid={`${sectionTestId}-search-clear-button`}
              type="button"
              aria-label={t('create.step2.filters.optionsClearSearch')}
              className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2.5 -translate-y-1/2 cursor-pointer"
              onClick={() => {
                setSearch('');
              }}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {showSelectAll && (
        <div
          className="flex items-center gap-3"
          title={selectAllLocked ? t('create.step2.filters.lockedLastValue') : undefined}
        >
          <Checkbox
            data-testid={`${sectionTestId}-select-all-checkbox`}
            id={`${def.key}-all`}
            checked={allSelected}
            disabled={selectAllLocked}
            onCheckedChange={() => {
              onToggleSelectAll(allOptions);
            }}
          />
          <Label
            htmlFor={`${def.key}-all`}
            className={cn(
              'text-sm font-medium',
              selectAllLocked ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
          >
            {t('create.step2.filters.selectAll')}
          </Label>
        </div>
      )}

      <div className={cn(showSearch && 'max-h-64 overflow-y-auto', 'space-y-2')}>
        {visible.length === 0 ? (
          <p
            data-testid={`${sectionTestId}-no-results`}
            className="text-muted-foreground py-4 text-center text-sm"
          >
            {t('create.step2.filters.optionsNoResults')}
          </p>
        ) : (
          visible.map((opt) => (
            <div
              key={opt}
              className="flex items-center gap-3"
              title={opt === pinnedOption ? t('create.step2.filters.lockedLastValue') : undefined}
            >
              <Checkbox
                data-testid={`${testId}-option-${def.key}-${toOptionTestIdSlug(opt)}`}
                data-value={opt}
                id={`${def.key}-${opt}`}
                checked={selected.includes(opt)}
                disabled={opt === pinnedOption}
                onCheckedChange={() => {
                  onToggleMultiValue(opt);
                }}
              />
              <Label
                data-testid={`${testId}-option-label-${def.key}-${toOptionTestIdSlug(opt)}`}
                htmlFor={`${def.key}-${opt}`}
                className={cn(
                  'text-sm font-normal break-words',
                  opt === pinnedOption ? 'cursor-not-allowed' : 'cursor-pointer',
                )}
              >
                {/* Display label only; `opt` stays the raw filter value. */}
                {labels.get(opt) ?? opt}
              </Label>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function FiltersLoading({ testId }: { testId: string }) {
  return (
    <div data-testid={`${testId}-loading`} className="space-y-2 px-6 py-4">
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-10 w-full rounded-md" />
      ))}
    </div>
  );
}

function FiltersError({ testId, onRetry }: { testId: string; onRetry: () => void }) {
  const { t } = useTranslation('experiments');
  return (
    <div
      data-testid={`${testId}-error`}
      className="flex flex-col items-center gap-2 px-6 py-10 text-center"
    >
      <div className="bg-destructive/10 text-destructive flex size-8 items-center justify-center rounded-full">
        <AlertCircle className="size-4" />
      </div>
      <p className="text-muted-foreground text-sm">{t('create.step2.filters.error')}</p>
      <Button
        data-testid={`${testId}-retry-button`}
        variant="outline"
        size="sm"
        className="mt-1 cursor-pointer"
        onClick={onRetry}
      >
        {t('create.step2.filters.retry')}
      </Button>
    </div>
  );
}

function FiltersEmpty({ testId }: { testId: string }) {
  const { t } = useTranslation('experiments');
  return (
    <p
      data-testid={`${testId}-empty`}
      className="text-muted-foreground px-6 py-10 text-center text-sm"
    >
      {t('create.step2.filters.empty')}
    </p>
  );
}
